import { useState, useMemo, useCallback } from 'react';
import { getDateKey } from '@/types';
import { useAppData } from '@/hooks/useAppData';
import { useTheme } from '@/hooks/useTheme';
import { useAuth, AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AppHeader } from '@/components/AppHeader';
import { DayCard } from '@/components/DayCard';
import { ExpensesChart } from '@/components/ExpensesChart';
import { GoalsChart } from '@/components/GoalsChart';
import { ProjectsPanel } from '@/components/ProjectsPanel';
import { BackupsSection } from '@/components/BackupsSection';
import { PromptsModal } from '@/components/PromptsModal';
import { AIChatModal } from '@/components/AIChatModal';
import { BarChart3, Target, ChevronDown, FolderOpen, Database } from 'lucide-react';
import { subDays } from 'date-fns';
import "@/App.css";

const INITIAL_DAYS = 7;
const LOAD_MORE = 7;

const Index = ({ theme, toggleTheme }) => {
  const data = useAppData();
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [visibleDaysCount, setVisibleDaysCount] = useState(INITIAL_DAYS);
  const [showChart, setShowChart] = useState(false);
  const [showGoals, setShowGoals] = useState(false);
  const [showProjects, setShowProjects] = useState(false);
  const [showBackups, setShowBackups] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);

  // AI Chat Modal state
  const [aiChat, setAiChat] = useState({ open: false, mode: null, sessionId: null, messages: [], loading: false });

  const today = new Date();
  const todayKey = getDateKey(today);

  const visibleDays = useMemo(() => {
    if (selectedDate) {
      const key = getDateKey(selectedDate);
      return [data.getDayData(key)];
    }
    const days = [];
    for (let i = 0; i < visibleDaysCount; i++) {
      const date = subDays(today, i);
      const key = getDateKey(date);
      days.push(data.getDayData(key));
    }
    return days;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, visibleDaysCount, data.version, todayKey]);

  const handleSelectDate = useCallback((date) => {
    setSelectedDate(date);
  }, []);

  const handleGoToday = useCallback(() => {
    setSelectedDate(undefined);
    setVisibleDaysCount(INITIAL_DAYS);
  }, []);

  const handleLoadMore = () => {
    setVisibleDaysCount(prev => prev + LOAD_MORE);
  };

  const handleGenerateSummary = useCallback(async (mode = 'general') => {
    // If same mode and already have a session, just reopen
    if (aiChat.sessionId && aiChat.mode === mode) {
      setAiChat(prev => ({ ...prev, open: true }));
      return;
    }
    // Start new session
    setAiChat({ open: true, mode, sessionId: null, messages: [], loading: true });
    try {
      const result = await data.startSummarySession(mode);
      setAiChat({
        open: true,
        mode,
        sessionId: result.session_id,
        messages: [{ role: 'assistant', content: result.response }],
        loading: false,
      });
    } catch (err) {
      setAiChat(prev => ({
        ...prev,
        loading: false,
        messages: [{ role: 'assistant', content: 'Error al generar el análisis. Intenta de nuevo.' }],
      }));
    }
  }, [aiChat.sessionId, aiChat.mode, data]);

  const handleStartSession = useCallback(async (mode) => {
    setAiChat({ open: true, mode, sessionId: null, messages: [], loading: true });
    try {
      const result = await data.startSummarySession(mode);
      setAiChat({
        open: true,
        mode,
        sessionId: result.session_id,
        messages: [{ role: 'assistant', content: result.response }],
        loading: false,
      });
    } catch (err) {
      setAiChat(prev => ({
        ...prev,
        loading: false,
        messages: [{ role: 'assistant', content: 'Error al generar el análisis. Intenta de nuevo.' }],
      }));
    }
  }, [data]);

  const handleSendMessage = useCallback(async (sessionId, message) => {
    // Optimistically add user message
    setAiChat(prev => ({
      ...prev,
      messages: [...prev.messages, { role: 'user', content: message }],
    }));
    try {
      const result = await data.sendSummaryMessage(sessionId, message);
      setAiChat(prev => ({
        ...prev,
        messages: [...prev.messages, { role: 'assistant', content: result.response }],
      }));
    } catch (err) {
      setAiChat(prev => ({
        ...prev,
        messages: [...prev.messages, { role: 'assistant', content: 'Error al procesar tu mensaje. Intenta de nuevo.' }],
      }));
    }
  }, [data]);

  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const year = currentYear;
  const month = currentMonth;
  const monthlyExpenses = data.getMonthlyExpenses(currentYear, currentMonth);
  const monthlyTotal = data.getMonthlyTotal(currentYear, currentMonth);
  const monthlyGoals = data.getMonthlyGoals(currentYear, currentMonth);
  const allGoals = data.getAllGoals();
  const allExpenses = data.getAllExpenses();
  const projects = data.getProjects();

  return (
    <div className="min-h-screen bg-background">
      <AppHeader
        selectedDate={selectedDate}
        onSelectDate={handleSelectDate}
        theme={theme}
        onToggleTheme={toggleTheme}
        onGenerateSummary={handleGenerateSummary}
        summaryLoading={aiChat.loading}
        onOpenPrompts={() => setShowPrompts(true)}
      />

      <PromptsModal
        open={showPrompts}
        onClose={() => setShowPrompts(false)}
        getPrompts={data.getPrompts}
        savePrompt={data.savePrompt}
        restorePrompt={data.restorePrompt}
      />

      <AIChatModal
        open={aiChat.open}
        onClose={() => setAiChat(prev => ({ ...prev, open: false }))}
        mode={aiChat.mode}
        startSession={handleStartSession}
        sendMessage={handleSendMessage}
        session={aiChat}
      />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-3">
        {/* Top bar: Proyectos | Objetivos del mes | Gastos del mes | Backups */}
        <div className="flex justify-end gap-2 flex-wrap overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <button
            className={`chart-toggle-btn ${showProjects ? 'active' : ''}`}
            onClick={() => setShowProjects(v => !v)}
            data-testid="toggle-projects-btn"
          >
            <FolderOpen className="h-3.5 w-3.5" />
            Proyectos
          </button>
          <button
            className={`chart-toggle-btn ${showGoals ? 'active' : ''}`}
            onClick={() => setShowGoals(v => !v)}
            data-testid="toggle-goals-chart-btn"
          >
            <Target className="h-3.5 w-3.5" />
            Objetivos
          </button>
          <button
            className={`chart-toggle-btn ${showChart ? 'active' : ''}`}
            onClick={() => setShowChart(v => !v)}
            data-testid="toggle-expenses-chart-btn"
          >
            <BarChart3 className="h-3.5 w-3.5" />
            Gastos
          </button>
          <button
            className={`chart-toggle-btn ${showBackups ? 'active' : ''}`}
            onClick={() => setShowBackups(v => !v)}
            data-testid="toggle-backups-btn"
          >
            <Database className="h-3.5 w-3.5" />
            Backups
          </button>
        </div>
        {/* Panels */}
        {(showProjects || showGoals || showChart || showBackups) && (
          <div className={`grid gap-3 ${
            [showProjects, showGoals, showChart, showBackups].filter(Boolean).length >= 2
              ? 'grid-cols-1 lg:grid-cols-2'
              : ''
          } ${
            [showProjects, showGoals, showChart, showBackups].filter(Boolean).length >= 3
              ? 'xl:grid-cols-3'
              : ''
          }`}>
            {showProjects && (
              <ProjectsPanel
                projects={projects}
                onCreateProject={data.createProject}
                onUpdateProject={data.updateProject}
                onDeleteProject={data.deleteProject}
              />
            )}
            {showGoals && <GoalsChart allGoals={allGoals} onUpdateProgress={data.setGoalProgressAbsolute} />}
            {showChart && <ExpensesChart allExpenses={allExpenses} projects={projects} />}
            {showBackups && <BackupsSection />}
          </div>
        )}

        {/* Daily cards */}

        {/* Back button when date selected */}
        {selectedDate && (
          <button className="back-btn" onClick={handleGoToday} data-testid="back-to-full-view-btn">
            &larr; Volver a la vista completa
          </button>
        )}

        {/* Day cards */}
        <div className="space-y-3">
          {visibleDays.map((day, index) => (
            <DayCard
              key={day.date}
              day={day}
              defaultOpen={index === 0}
              onAddTodo={data.addTodo}
              onToggleTodo={data.toggleTodo}
              onDeleteTodo={data.deleteTodo}
              onSetTodoDeadline={data.setTodoDeadline}
              onAddNote={data.addNote}
              onUpdateNote={data.updateNote}
              onDeleteNote={data.deleteNote}
              onAddExpense={data.addExpense}
              onDeleteExpense={data.deleteExpense}
              onAddGoal={data.addGoal}
              onUpdateGoalProgress={data.updateGoalProgress}
              onSetGoalDeadline={data.setGoalDeadline}
              onUpdateGoalTitle={data.updateGoalTitle}
              onDeleteGoal={data.deleteGoal}
              projects={projects}
            />
          ))}
        </div>

        {/* Load more */}
        {!selectedDate && (
          <div className="flex justify-center py-4">
            <button className="load-more-btn" onClick={handleLoadMore} data-testid="load-more-btn">
              <ChevronDown className="h-4 w-4" />
              Cargar más días
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

function App() {
  const { theme, toggleTheme } = useTheme();

  return (
    <AuthProvider>
      <ProtectedRoute>
        <Index theme={theme} toggleTheme={toggleTheme} />
      </ProtectedRoute>
    </AuthProvider>
  );
}

export default App;
